import { useAuth } from "@/src/contexts/auth.context";
import { useOrgFeature } from "@/src/services/organization/org-features.hooks";
import { BlocksUser } from "@/src/models/user.model";
import { useTheme } from "@/src/styles/theme-provider";
import { Router } from "expo-router";
import {
  ArrowSwapVertical,
  Book1,
  CloseCircle,
  Cup,
  Element,
  LogoutCurve,
  Notification,
  Profile2User,
  Setting3,
  Star1,
} from "iconsax-react-nativejs";
import React from "react";
import { useTranslation } from "react-i18next";
import { Image, Modal, Pressable, ScrollView, Text, View } from "react-native";
import DropDown, { ContextMenuItem } from "../ui-kit/drop-down";
import UserAvatar from "../ui-kit/user-avatar";

type MoreMenuModalProps = Readonly<{
  visible: boolean;
  onClose: () => void;
  t: (key: string) => string;
  router: Router;
  user: BlocksUser | null;
  isActive: (path: string) => boolean;
  isLoggingOut: boolean;
  onLogoutPress: () => void;
  onSwitchAccount: () => void;
  onSwitchOrg: () => void;
}>;

type NavRowProps = Readonly<{
  icon: React.ReactElement;
  label: string;
  active: boolean;
  onPress: () => void;
}>;

const NavRow = ({ icon, label, active, onPress }: NavRowProps) => {
  const { themeVars } = useTheme();

  const iconColor = active
    ? (themeVars["--color-primary-default"] as string)
    : (themeVars["--color-slate-default"] as string);

  const textColor = active
    ? (themeVars["--color-primary-default"] as string)
    : (themeVars["--color-onyx-default"] as string);

  return (
    <Pressable
      onPress={onPress}
      className={`flex-row items-center gap-4 px-4 py-3 rounded-xl
        ${active ? "bg-primary-2 web:hover:bg-primary-2" : "active:bg-ash-3 web:hover:bg-ash-3"}
      `}
    >
      {React.cloneElement(icon, { size: 22, color: iconColor } as any)}
      <Text className="text-sm font-medium" style={{ color: textColor }}>
        {label}
      </Text>
    </Pressable>
  );
};

const MoreMenuModal = ({
  visible,
  onClose,
  router,
  user,
  isActive,
  isLoggingOut,
  onLogoutPress,
  onSwitchAccount,
  onSwitchOrg,
}: MoreMenuModalProps) => {
  const { t } = useTranslation();
  const { themeVars, varStyle } = useTheme();

  const { appRole, isAdmin, isSuperAdmin } = useAuth();
  const normalizedRole = (appRole ?? "").toLowerCase().replace(/[\s_-]/g, "");

  const userRoleLabel = isSuperAdmin
    ? t("LABEL_SUPER_ADMIN")
    : normalizedRole === "admin"
      ? t("LABEL_ADMIN")
      : t("LABEL_MEMBER");

  const isAdminOrAbove = isAdmin;
  const { enabled: leaderboardEnabled } = useOrgFeature("leaderboard");

  const profileMenuItems: readonly ContextMenuItem[] = [
    {
      label: t("BTN_SWITCH_CLUB"),
      onPress: () => {
        onClose();
        onSwitchOrg();
      },
    },
    {
      label: t("BTN_SWITCH_ACCOUNT"),
      onPress: () => {
        onClose();
        onSwitchAccount();
      },
    },
    {
      label: t("BTN_LOGOUT"),
      onPress: () => {
        onClose();
        onLogoutPress();
      },
      destructive: true,
      rightIcon: (
        <LogoutCurve size={16} color={themeVars["--color-crimson-default"]} />
      ),
    },
  ];

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent
      onRequestClose={onClose}
    >
      <Pressable className="flex-1 bg-black/50" onPress={onClose}>
        <Pressable
          style={varStyle}
          className="absolute bottom-0 left-0 right-0 bg-chalk rounded-t-3xl max-h-[85%]"
          onPress={(e) => e.stopPropagation()}
        >
          <View className="px-4 pt-2.5 pb-8">
            {/* Grabber handle */}
            <View className="self-center w-10 h-1 rounded-full bg-ash-3 mb-3" />
            {/* Header */}
            <View className="flex-row justify-between items-center mb-4">
              <View className="flex-row items-center gap-2">
                <Image
                  resizeMode="cover"
                  style={{ width: 24, height: 24 }}
                  source={require("@/src/assets/images/club_logo.png")}
                />
                <Text className="font-bold text-lg text-onyx">
                  {t("LABEL_SELISE_CLUB")}
                </Text>
              </View>
              <Pressable
                onPress={onClose}
                hitSlop={8}
                className="p-1.5 rounded-full web:hover:bg-ash-3"
              >
                <CloseCircle
                  size={28}
                  color={themeVars["--color-slate-default"]}
                />
              </Pressable>
            </View>

            <ScrollView showsVerticalScrollIndicator={false}>
              <View className="gap-0.5">
                {isAdminOrAbove && (
                  <NavRow
                    icon={<Profile2User />}
                    label={t("BTN_USERS")}
                    active={isActive("/uam")}
                    onPress={() => {
                      onClose();
                      router.push("/uam");
                    }}
                  />
                )}
                <NavRow
                  icon={<Book1 />}
                  label={t("BTN_WIKI")}
                  active={isActive("/wiki")}
                  onPress={() => {
                    onClose();
                    router.push("/(tabs)/wiki/wiki-articles");
                  }}
                />
                {leaderboardEnabled && (
                  <NavRow
                    icon={<Cup />}
                    label={t("BTN_LEADERBOARD")}
                    active={isActive("/leaderboard")}
                    onPress={() => {
                      onClose();
                      router.push("/leaderboard");
                    }}
                  />
                )}
                <NavRow
                  icon={<Star1 />}
                  label={t("BTN_REWARDLY")}
                  active={isActive("/rewardly")}
                  onPress={() => {
                    onClose();
                    router.push("/rewardly");
                  }}
                />
                {isAdminOrAbove && (
                  <NavRow
                    icon={<Element />}
                    label={t("BTN_DASHBOARD")}
                    active={isActive("/dashboard")}
                    onPress={() => {
                      onClose();
                      router.push("/dashboard");
                    }}
                  />
                )}
                <NavRow
                  icon={<Notification />}
                  label={t("BTN_NOTIFICATION")}
                  active={isActive("/notification")}
                  onPress={() => {
                    onClose();
                    router.push("/notification");
                  }}
                />
                <NavRow
                  icon={<Setting3 />}
                  label={t("BTN_SETTINGS")}
                  active={isActive("/settings")}
                  onPress={() => {
                    onClose();
                    router.push("/settings");
                  }}
                />

                <View className="border-t border-ash-3 my-2" />

                {/* User Profile Row */}
                <View className="flex-row items-center justify-between px-4 py-2.5 rounded-2xl">
                  <Pressable
                    onPress={() => {
                      onClose();
                      router.push(`/profile/${user?.itemId}`);
                    }}
                    className="flex-row items-center gap-3 flex-1 active:opacity-70 web:hover:bg-ash-3 web:hover:rounded-lg"
                  >
                    <View className="rounded-full overflow-hidden">
                      {user?.profileImageUrl ? (
                        <Image
                          source={{ uri: user.profileImageUrl }}
                          style={{ width: 40, height: 40 }}
                          resizeMode="cover"
                        />
                      ) : (
                        <UserAvatar
                          firstName={user?.firstName ?? undefined}
                          lastName={user?.lastName ?? undefined}
                          size={40}
                        />
                      )}
                    </View>
                    <View>
                      <Text className="font-semibold text-sm text-onyx">
                        {user?.firstName} {user?.lastName}
                      </Text>
                      <Text className="text-xs text-slate">
                        {userRoleLabel}
                      </Text>
                    </View>
                  </Pressable>

                  <DropDown
                    items={[...profileMenuItems]}
                    menuWidth={210}
                    trigger={
                      <View className="p-1.5 rounded-lg bg-ash">
                        <ArrowSwapVertical
                          size={18}
                          color={themeVars["--color-slate-default"]}
                        />
                      </View>
                    }
                  />
                </View>
              </View>
            </ScrollView>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

export default MoreMenuModal;
